import { ReactElement, use, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import theme from 'style/theme';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import PageHeader from 'shared/components/page-header/page-header';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';
import OrganisationNavigationQueryParameters from 'shared/constants/organisation-navigation-query-parameters';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import OrganisationTreeNode from 'shared/features/organisation/models/organisation-tree-node';
import { OrganisationUnitTreeProvider } from 'shared/features/organisation/providers/organisation-unit-tree-provider';
import usePrevious from 'shared/hooks/previous';

import { useOrganisationUnitsProvider } from 'features/organisation/context/organisation-units-provider';
import OrganisationDetails from './organisation-details/organisation-details.component';
import OrganisationTree from './organisation-tree/organisation-tree.component';

import './organisation.scss';

const Organisation = (): ReactElement => {
  const { t } = useTranslation('organisation');
  const { hasPermission } = usePermissionAuthorization();
  const location = useLocation();
  const { showDefaultConfirmationPopUp } = use(ConfirmationPopupProviderContext);
  const {
    organisationUnitDetailsHook: { form },
  } = useOrganisationUnitsProvider();

  const [viewingMode, setViewingMode] = useState<ViewingMode>('none');
  const [detailNode, setDetailNode] = useState<OrganisationTreeNode | undefined>();
  const [selectedNode, setSelectedNode] = useState<OrganisationTreeNode | undefined>();
  const [upstreamParentBranch, setUpstreamParentBranch] = useState<OrganisationTreeNode[]>([]);
  const [siblings, setSiblings] = useState<OrganisationTreeNode[]>([]);
  const organisationUnitId = new URLSearchParams(location.search).get(OrganisationNavigationQueryParameters.OrganisationUnitId) ?? undefined;

  const selectOrganisationUnit = (
    newSelectedNode: OrganisationTreeNode | undefined,
    upstreamParentBranch: OrganisationTreeNode[],
    siblings: OrganisationTreeNode[],
    viewingMode: ViewingMode,
  ): void => {
    setSelectedNode(newSelectedNode);
    setDetailNode(newSelectedNode);
    setUpstreamParentBranch(upstreamParentBranch);
    setSiblings(siblings);
    setViewingMode(viewingMode);
  };

  const handleSelectOrganisationUnit = (
    newSelectedNode: OrganisationTreeNode | undefined,
    upstreamParentBranch: OrganisationTreeNode[],
    siblings: OrganisationTreeNode[],
    viewingMode: ViewingMode,
  ): void => {
    showDefaultConfirmationPopUp({
      shouldShow: !form.formState.isSubmitted && form.formState.isDirty,
      action: () => {
        selectOrganisationUnit(newSelectedNode, upstreamParentBranch, siblings, viewingMode);
      },
    });
  };

  const handleStartCreation = (): void => {
    setDetailNode(undefined);
    setViewingMode('creation');
  };

  const previousSelectedNode = usePrevious(selectedNode);

  const handleCancel = (): void => {
    if (viewingMode === 'creation') {
      setDetailNode(previousSelectedNode);
    }
    setViewingMode('viewing');
  };

  return (
    <OrganisationUnitTreeProvider.WithFaults>
      <div className="organisation-container">
        <div
          className={`organisation-structure-container ${viewingMode !== 'none' && 'detail-is-opened'}`}
          style={{
            transition: theme.transitions.create('all', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest,
            }),
          }}
        >
          <PageHeader
            title={t('pageTitle')}
            addBtnVisible={(viewingMode === 'viewing' || viewingMode === 'none') && hasPermission(Permission.CreateOrganisationUnit)}
            onAdd={handleStartCreation}
            addBtnDisabled={selectedNode === undefined || selectedNode.depth === 20}
          />
          <OrganisationTree
            sessionStorageKey={FilterSessionStorageKeys.ORGANISATION_TREE_EXPANDED_NODES}
            initialSelectedOrganisationUnit={organisationUnitId}
            onSelectOrganisationUnit={(selectedNode, upstreamParentBranch, siblings) =>
              handleSelectOrganisationUnit(selectedNode, upstreamParentBranch, siblings, 'viewing')
            }
            onDeselectOrganisationUnit={() => handleSelectOrganisationUnit(undefined, [], [], 'none')}
          />
        </div>
        <div
          className={`organisation-unit-detail-container ${viewingMode !== 'none' && 'detail-is-opened'}`}
          style={{
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest,
            }),
          }}
        >
          <OrganisationDetails
            viewingMode={viewingMode}
            upstreamParentBranch={upstreamParentBranch}
            siblings={siblings}
            cancelAction={handleCancel}
            successAction={() => setViewingMode('viewing')}
            onViewingModeChanged={(viewingMode) => setViewingMode(viewingMode)}
            selectedNode={detailNode}
            parentNode={viewingMode === 'creation' ? selectedNode : selectedNode?.parentOrganisationUnit}
          />
        </div>
      </div>
    </OrganisationUnitTreeProvider.WithFaults>
  );
};

export default Organisation;
